import { PLATFORM_CATALOG, type Platform } from '@shopx-distribution/shared';
import { useState } from 'react';

import { cn } from '@/lib/utils.js';

/**
 * The logo a GraFx media connector publishes for its system, so a destination
 * carries the same mark here as it does in Studio.
 *
 * Those logos are hosted by CHILI publish and, in two cases, by the vendor, so
 * a fetch can fail for reasons that have nothing to do with this extension. The
 * initial tile below is what every destination without a published icon gets
 * anyway, which makes it a fallback rather than a broken-image placeholder.
 */
export function PlatformIcon({ platform, className }: { platform: Platform; className?: string }) {
  const { label, iconUrl } = PLATFORM_CATALOG[platform];
  const [failed, setFailed] = useState(false);

  const frame = cn(
    'flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-brand border border-line bg-subtle',
    className,
  );

  if (!iconUrl || failed) {
    return (
      <span className={frame} aria-hidden="true">
        <span className="text-xs font-semibold text-muted">{initials(label)}</span>
      </span>
    );
  }

  return (
    <span className={frame}>
      <img
        src={iconUrl}
        alt=""
        loading="lazy"
        className="h-5 w-5 object-contain"
        onError={() => setFailed(true)}
      />
    </span>
  );
}

function initials(label: string): string {
  return label
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}
